import { HttpLink } from 'apollo-angular/http';
import { HttpClient } from '@angular/common/http';
import { Injectable, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import { ModelHelper } from '@dentsply-sirona/utilities';
import { TranslateService } from '@ngx-translate/core';

import * as _ from 'lodash';
import { ToastrService } from 'ngx-toastr';
import { Observable } from 'rxjs';
import 'rxjs/add/operator/map';
import { map } from 'rxjs/operators';
import { ConfigService } from 'src/app/modules/core/services/config.service';
import { LoaderService } from 'src/app/modules/core/services/loader.service';
import { NotifyOrConfirmService } from 'src/app/modules/core/services/notify-or-confirm.service';
import { CompanyState } from '../enums/company-states.enum';
import { SortDirection } from '../enums/sort-direction.enum';
import { RadialSearch } from '../interfaces/radial-search.interface';
import { UpdateServiceInterface } from '../interfaces/update-service.interface';
import { AllCompanyElements } from '../models/all-company-elements.model';
import { Company } from '../models/company.model';
import { ListRequest } from '../models/list-request.model';
import { WhereFilter } from '../models/where-filter.model';
import { CompanySchema } from '../schemas/company.schema';
import { UserSchema } from '../schemas/user.schema';
import { ApiObservable } from '../types/api-observable.type';
import { GraphqlService } from './graphql.service';
import { ListService } from './list.service';
import { LoggerService } from './logger.service';
import { SessionService } from './session.service';
import { WindowRefService } from './window.service';

@Injectable({
	providedIn: 'root'
})
export class CompanyService extends GraphqlService implements UpdateServiceInterface {
	/**
	 * Constructor
	 */
	constructor(
		private listService: ListService,
		protected http: HttpClient,
		protected httpLink: HttpLink,
		protected configService: ConfigService,
		protected loaderService: LoaderService,
		protected notifyOrConfirmService: NotifyOrConfirmService,
		protected sessionService: SessionService,
		protected translateService: TranslateService,
		protected router: Router,
		protected logger: LoggerService,
		protected ngZone: NgZone,
		protected toastrService: ToastrService,
		protected windowRef: WindowRefService
	) {
		// Init GraphqlService
		super(
			http,
			httpLink,
			configService,
			loaderService,
			notifyOrConfirmService,
			sessionService,
			translateService,
			router,
			logger,
			ngZone,
			toastrService,
			windowRef
		);
	}

	/**
	 * Get favorites recipients / contractors
	 */
	getFavoriteContractors(): Observable<Company[]> {
		return new Observable<Company[]>((subscriber) => {
			// Check companyUid
			if (!_.get(this.sessionService, 'currentUser.uid')) {
				subscriber.error('Missing uid of current user');
				return;
			}

			// Get full company data via user
			this.query(UserSchema.getUserCompanySchema(CompanySchema.favoriteContractorProperties), {
				uid: this.sessionService.currentUser.uid
			})
				.pipe(
					map((data: any) => {
						// Check data
						if (!_.get(data, 'user.company.favoriteContractors')) {
							return [];
						}

						// Map favorite contractors in company data of user
						return ModelHelper.mapArray(Company, data.user.company.favoriteContractors);
					})
				)
				.subscribe((contractors) => {
					// Return favorite contractors
					subscriber.next(contractors);
					subscriber.complete();
				});
		});
	}

	/**
	 * Get companies
	 */
	getCompanyList(
		filters: WhereFilter[] = [],
		pageIndex: number = 0,
		resultsPerPage: number = 10,
		orderBy?: string,
		isAscending?: string
	): Observable<{ totalCount: number; list: Company[] }> {
		return this.query(CompanySchema.companyList, {
			filters,
			pageIndex,
			resultsPerPage,
			orderBy,
			isAscending
		}).pipe(
			map((data: any) => ({
				totalCount: data.companylist.totalCount,
				list: ModelHelper.mapArray<Company>(Company, data.companylist.list)
			}))
		);
	}

	/**
	 * Fügt die Empänger/ Labor zur Favoritenliste der Company hinzu
	 *
	 * @param favoriteContractorUid Uid des Empfängers
	 */
	addFavoriteContractor(favoriteContractorUid: string): Observable<Company[]> {
		return this.mutation(CompanySchema.addFavoriteContractor, {
			favoriteContractorUid
		}).pipe(map((data: any) => ModelHelper.mapArray(Company, data.addFavoriteContractor.favoriteContractors)));
	}

	/**
	 * Get favorites recipients / contractors
	 * Entfernt die Empänger aus der Favoritenliste der Company
	 *
	 * @param favoriteContractorUid Uid des Empfängers
	 */
	removeFavoriteContractor(favoriteContractorUid: string): Observable<Company[]> {
		return this.mutation(CompanySchema.removeFavoriteContractor, {
			favoriteContractorUid
		}).pipe(map((data: any) => ModelHelper.mapArray(Company, data.removeFavoriteContractor.favoriteContractors)));
	}

	/**
	 * Update company
	 *
	 * Hint:
	 * The API no longer requires a UID, but the UpdateService interface, so the parameter remains included. This is ignored in the schema.
	 */
	update(uid: string, data: any, schema: any = CompanySchema.updateCompany): ApiObservable<Company> {
		if (data.languageCode) {
			data['language'] = {
				code: data.languageCode
			};
		}

		if (data.shippingAddressIsEqualToBillingAddress) {
			delete data.shippingAddress;
		}

		return this.mutation(schema, {
			company: data
		}).pipe(map((response: any) => Company.map(response.updateCompany)));
	}

	/**
	 * Get company with official data by uid
	 */
	getOfficialDataByUid(companyUid: string): Observable<Company> {
		// Get company with official data
		return this.query(CompanySchema.getByUid, {
			uid: companyUid
		}).pipe(map((data: any) => Company.map(data.company)));
	}

	/**
	 * Get company (with full data) of current user
	 *
	 * @param schema Schema to get the properties of the company
	 */
	getCompanyOfCurrentUser(schema: any = CompanySchema.fullLevelProperties): Observable<Company> {
		return this.query(UserSchema.getUserCompanySchema(schema), {
			uid: this.sessionService.currentUser.uid
		}).pipe(
			map((data: any) =>
				// Map company data of user
				Company.map(data.user.company)
			)
		);
	}

	/**
	 * Get company for company edit view
	 */
	getCompanyForWelcome(): Observable<Company> {
		return this.getCompanyOfCurrentUser(CompanySchema.companyForWelcome);
	}

	/**
	 * Get company for company edit view
	 */
	getCompanyForEdit(): Observable<Company> {
		return this.getCompanyOfCurrentUser(CompanySchema.companyForEditProperties);
	}

	/**
	 * Get company for company edit address view
	 */
	getCompanyForEditAddress(): Observable<Company> {
		return this.getCompanyOfCurrentUser(CompanySchema.companyForEditAddressProperties);
	}

	/**
	 * Get company with LabLock data
	 */
	getCompanyWithLabLockEntries(): Observable<Company> {
		return this.getCompanyOfCurrentUser(CompanySchema.labLockProperties);
	}

	/**
	 * Get company with AdditionalOrderInformation
	 */
	getCompanyForAdditionalOrderInformation(): Observable<Company> {
		return this.getCompanyOfCurrentUser(CompanySchema.companyForAdditionalOrderInformation);
	}

	/**
	 * Set company status
	 */
	setState(companyUid: string, state: CompanyState): Observable<Company> {
		return this.mutation(CompanySchema.companySetState, {
			uid: companyUid,
			state
		}).pipe(map((data: any) => Company.map(data.companySetState)));
	}

	/**
	 * Get all information for lab profile page
	 */
	getAllCompanyElements(): Observable<AllCompanyElements> {
		// Configuration
		const lists: (string | ListRequest)[] = [
			ListRequest.map({ id: 'offerings', query: 'offeringlist' }),
			ListRequest.map({ id: 'services', query: 'servicelist' }),
			ListRequest.map({ id: 'materialClasses', query: 'labprofilematerialclasslist' }),
			ListRequest.map({ id: 'restorations', query: 'labprofilerestorationtypelist' }),
			ListRequest.map({ id: 'shades', properties: ['guide', 'uid'], query: 'labprofileshadelist' })
		];

		// Request
		return this.listService.getLists(lists).pipe(map((response) => AllCompanyElements.map(response)));
	}

	/**
	 * Get all information for lab profile page in one Request
	 */
	getAllCompanyElementsInOneQuery(): Observable<AllCompanyElements> {
		// Configuration
		const lists: ListRequest[] = [
			ListRequest.map({ id: 'offerings', query: 'offeringlist' }),
			ListRequest.map({ id: 'services', query: 'servicelist' }),
			ListRequest.map({ id: 'materialClasses', query: 'labprofilematerialclasslist' }),
			ListRequest.map({ id: 'restorations', query: 'labprofilerestorationtypelist' }),
			ListRequest.map({ id: 'shades', properties: ['guide', 'uid'], query: 'labprofileshadelist' })
		];

		// Request
		return this.listService.getListsInOneQuery(lists).pipe(map((response) => AllCompanyElements.map(response)));
	}

	/**
	 * Holt relevanten Information für die Suche der bevorzugten Empfänger
	 */
	getOfferAndServiceElements(): Observable<AllCompanyElements> {
		// Configuration
		const lists: (string | ListRequest)[] = [
			ListRequest.map({ id: 'offerings', query: 'offeringlist' }),
			ListRequest.map({ id: 'services', query: 'servicelist' })
		];

		// Request
		return this.listService.getLists(lists).pipe(map((response) => AllCompanyElements.map(response)));
	}

	/**
	 * Get FavorizingPurchaser List from User
	 *
	 * @param includeSleepingLabs
	 * @param whereFilter
	 * @param pageIndex
	 * @param resultsPerPage
	 * @param onlyTotalCount
	 * @param orderBy
	 * @param sortDirection
	 */
	getFavorizingPurchasers(
		includeSleepingLabs?: boolean,
		whereFilter?: WhereFilter[],
		pageIndex?: number,
		resultsPerPage?: any,
		onlyTotalCount?: boolean,
		orderBy?: string,
		sortDirection?: SortDirection
	): Observable<{ list: Company[]; totalCount: number }> {
		return this.query(CompanySchema.getFavorizingPurchasers, {
			includeSleepingLabs: includeSleepingLabs === undefined ? false : includeSleepingLabs,
			whereFilter,
			pageIndex,
			resultsPerPage,
			onlyTotalCount,
			orderBy,
			sortDirection
		}).pipe(
			map((data: any) => ({
				list: ModelHelper.mapArray(Company, data.getFavorizingPurchasers.list),
				totalCount: data.getFavorizingPurchasers.totalCount
			}))
		);
	}

	/**
	 * Remove favorizing sender / purchasers
	 */
	removeFavorizingPurchaser(favorizingPurchaserUid: string): Observable<Company[]> {
		return this.mutation(CompanySchema.removeFavorizingPurchaser, {
			favorizingPurchaserUid
		}).pipe(map((data: any) => ModelHelper.mapArray(Company, data.removeFavorizingPurchaser)));
	}

	/**
	 * Search for recipients / contractors
	 */
	searchContractors(
		radialSearch?: RadialSearch,
		serviceFilter?: string[],
		offeringFilter?: string[],
		whereFilter?: WhereFilter[],
		pageIndex?: number,
		resultsPerPage?: any,
		onlyTotalCount?: boolean,
		orderBy?: string,
		sortDirection?: SortDirection
	): Observable<{ list: Company[]; totalCount: number }> {
		return this.query(CompanySchema.searchContractors, {
			radialSearch,
			serviceFilter,
			offeringFilter,
			whereFilter,
			pageIndex,
			resultsPerPage,
			onlyTotalCount,
			orderBy,
			sortDirection
		}).pipe(
			map((data: any) => ({
				list: ModelHelper.mapArray(Company, data.searchContractors.list),
				totalCount: data.searchContractors.totalCount
			}))
		);
	}
}
